import { canonicalEventTypeForDisplay } from './activityEventLabels';

/** Mobil screen_view — teknik ekran adı → Türkçe görünen ad */

const SCREEN_NAME_MAP = {
  ProfilimMain: 'Profilim',
  profilimmain: 'Profilim',
  LoginForm: 'Giriş',
  loginform: 'Giriş',
  ForgotPassword: 'Şifremi Unuttum',
  forgotpassword: 'Şifremi Unuttum',
  SmsVerification: 'SMS Doğrulama',
  smsverification: 'SMS Doğrulama',
  SetNewPassword: 'Yeni Şifre Belirleme',
  setnewpassword: 'Yeni Şifre Belirleme',
  PasswordSuccess: 'Şifre Başarı',
  passwordsuccess: 'Şifre Başarı',
  'Şifre Başarı Ekranı': 'Şifre Başarı',
  'şifre başarı ekranı': 'Şifre Başarı',
  GetPassword: 'Şifre Giriş',
  getpassword: 'Şifre Giriş',
  Anasayfa: 'Ana Sayfa',
  anasayfa: 'Ana Sayfa',
  WebViewPage: 'Alışveriş',
  webviewpage: 'Alışveriş',
  Islemler: 'İşlemler',
  islemler: 'İşlemler',
  İşlemler: 'İşlemler',
  'işlemler': 'İşlemler',
  Notifications: 'Bildirimler',
  notifications: 'Bildirimler',
  Kozakartim: 'Kozakartım',
  kozakartim: 'Kozakartım',
  Kozakartım: 'Kozakartım',
  KisiselBilgilerim: 'Kişisel Bilgilerim',
  kisiselbilgilerim: 'Kişisel Bilgilerim',
  OdemeSayfasi: 'Ödeme',
  odemesayfasi: 'Ödeme',
  BiziPuanla: 'Bizi Puanla',
  bizipuanla: 'Bizi Puanla',
  // Küçük harfe çevrilirken Türkçe harfleri düşmüş gelen adlar
  ilemler: 'İşlemler',
  kozakartm: 'Kozakartım',
  // Sekme / modal / sonuç ekranları (anahtarlar küçük harf; arama küçük harfle de yapılır)
  upcoming_installments_tab: 'Yaklaşan Taksitler',
  all_transactions_tab: 'Tüm İşlemler',
  payment_method_modal: 'Ödeme Yöntemi Seçimi',
  payment_result: 'Ödeme Sonucu',
  payment_3ds: '3D Secure Doğrulama',
  campaign_detail: 'Kampanya Detayı',
  form: 'Form',
  borc: 'Borç',
  onboarding: 'Tanıtım',
  qrkod: 'QR Kod',
  authcomingsoon: 'Yakında',
  splashscreen: 'Açılış',
  bizeulasin: 'Bize Ulaşın',
};

const SCREEN_VIEW_EVENT_TYPES = new Set(['screen_view', 'screenview']);

/** API / ödeme / limit logları — path alanı ekran adı değildir */
const NON_SCREEN_EVENT_TYPES = new Set([
  'limit_increase_requested',
  'limit_increase_confirmed',
  'payment_modal_open',
  'payment_method_selected',
  'customer_rating_submitted',
  'upcoming_installments_view',
  'upcoming_installments_detail_click',
  'transactions_view_all_click',
  'transactions_filter_apply',
  'contact_action_click',
  'user_login',
  'user_logout',
  'user_register',
  'user_registered',
  'login_option_click',
  'notification_sent',
  'notification_read',
  'campaign_clicked',
]);

function isLikelyHttpApiPath(value) {
  if (!value || typeof value !== 'string') return false;
  const t = value.trim();
  if (!t.startsWith('/')) return false;
  return /\/[\d-]/.test(t) || t.includes('limit-increase') || t.includes('register-token');
}

function isMappedScreenName(value) {
  if (!value) return false;
  const t = String(value).trim();
  return !!(SCREEN_NAME_MAP[t] || SCREEN_NAME_MAP[t.toLowerCase()]);
}

/** Backend bazen eventType alanına doğrudan ekran adı yazar (LoginForm, GetPassword…) */
function looksLikeMobileScreenComponent(value) {
  if (!value || typeof value !== 'string') return false;
  const t = value.trim();
  if (SCREEN_VIEW_EVENT_TYPES.has(t.toLowerCase())) return false;
  if (isMappedScreenName(t)) return true;
  // PascalCase mobil ekran bileşenleri: LoginForm, ProfilimMain
  return /^[A-Z][a-zA-Z0-9]{2,}$/.test(t);
}

export function resolveScreenNameFromActivityData(activityData, eventType = null) {
  if (!activityData || typeof activityData !== 'object') return null;

  const ev = activityData.eventData || activityData.EventData;
  const et = eventType ? String(eventType).toLowerCase() : '';
  const allowRouteFields =
    !et || SCREEN_VIEW_EVENT_TYPES.has(et) || looksLikeMobileScreenComponent(eventType);

  const pick = (value) => {
    if (value === null || value === undefined || value === '') return null;
    const s = String(value).trim();
    if (!s) return null;
    if (isLikelyHttpApiPath(s)) return null;
    return s;
  };

  const candidates = [
    pick(activityData.screenName),
    pick(activityData.screen_name),
    pick(activityData.page),
    pick(activityData.screen),
    pick(ev?.screenName),
    pick(ev?.screen_name),
    pick(ev?.page),
    pick(activityData.data?.screenName),
    pick(activityData.data?.screen_name),
    pick(activityData.data?.page),
  ];

  if (allowRouteFields) {
    candidates.push(
      pick(activityData.route),
      pick(activityData.path),
      pick(ev?.route),
      pick(activityData.data?.route),
    );
  }

  for (const c of candidates) {
    if (c) return c;
  }
  return null;
}

export function isScreenViewLikeEvent(eventType, activityData = null) {
  const canonical = canonicalEventTypeForDisplay(eventType);
  if (canonical && NON_SCREEN_EVENT_TYPES.has(canonical)) {
    return false;
  }
  if (eventType && SCREEN_VIEW_EVENT_TYPES.has(String(eventType).toLowerCase())) {
    return true;
  }
  if (resolveScreenNameFromActivityData(activityData, eventType)) {
    return true;
  }
  return looksLikeMobileScreenComponent(eventType);
}

export function toReadableScreenName(rawName) {
  if (!rawName) return '';
  const normalized = String(rawName).trim();
  if (SCREEN_NAME_MAP[normalized]) return SCREEN_NAME_MAP[normalized];

  const lower = normalized.toLowerCase();
  if (SCREEN_NAME_MAP[lower]) return SCREEN_NAME_MAP[lower];

  const onboardingStep = lower.match(/^onboarding_step_(\d+)$/);
  if (onboardingStep) return `Tanıtım ${onboardingStep[1]}. Adım`;

  return normalized
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .trim();
}

const TURKISH_VOWELS = 'aeıioöuü';

function findLastTurkishVowel(text) {
  for (let i = text.length - 1; i >= 0; i -= 1) {
    const c = text[i].toLocaleLowerCase('tr-TR');
    if (TURKISH_VOWELS.includes(c)) return c;
  }
  return 'a';
}

/** "Ana Sayfa" → "Ana Sayfa'yı" (belirtme hâli) */
export function addTurkishAccusativeSuffix(name) {
  const trimmed = String(name || '').trim();
  if (!trimmed) return trimmed;

  const lastChar = trimmed.slice(-1).toLocaleLowerCase('tr-TR');
  const lastVowel = findLastTurkishVowel(trimmed);
  const vowelHarmony = {
    a: { vowel: 'yı', consonant: 'ı' },
    ı: { vowel: 'yı', consonant: 'ı' },
    e: { vowel: 'yi', consonant: 'i' },
    i: { vowel: 'yi', consonant: 'i' },
    o: { vowel: 'yu', consonant: 'u' },
    u: { vowel: 'yu', consonant: 'u' },
    ö: { vowel: 'yü', consonant: 'ü' },
    ü: { vowel: 'yü', consonant: 'ü' },
  };
  const harmony = vowelHarmony[lastVowel] || vowelHarmony.a;
  const suffix = TURKISH_VOWELS.includes(lastChar) ? harmony.vowel : harmony.consonant;
  return `${trimmed}'${suffix}`;
}

function normalizeScreenLabelForPhrase(label) {
  return String(label || '')
    .trim()
    .replace(/\s+[Ee]kran(ı|i)?\s*$/u, '')
    .replace(/\s+[Ss]ayfas(ı|i)?\s*$/u, '')
    .trim();
}

function buildScreenViewUserDetailPhrase(readable) {
  const label = normalizeScreenLabelForPhrase(readable) || readable;
  return `${label} ekranını görüntüledi`;
}

/** Kullanıcı detay sayfası: "Ana Sayfa ekranını görüntüledi" */
export function getScreenViewUserDetailPhrase(activityData, eventTypeFallback = null) {
  const raw =
    resolveScreenNameFromActivityData(activityData, eventTypeFallback) ||
    (looksLikeMobileScreenComponent(eventTypeFallback) ? eventTypeFallback : null);

  if (!raw) return 'Sayfa görüntüledi';

  const rawLower = String(raw).trim().toLowerCase();
  if (rawLower === 'webviewpage') {
    const sourceScreen = toReadableScreenName(
      activityData?.sourceScreen || activityData?.source_screen || '',
    );
    const base = buildScreenViewUserDetailPhrase('Alışveriş');
    if (sourceScreen) {
      return `${base} (${sourceScreen})`;
    }
    return base.charAt(0).toLocaleUpperCase('tr-TR') + base.slice(1);
  }

  const readable = toReadableScreenName(raw);
  if (!readable) return 'Sayfa görüntüledi';

  const phrase = buildScreenViewUserDetailPhrase(readable);
  return phrase.charAt(0).toLocaleUpperCase('tr-TR') + phrase.slice(1);
}

/** Anlık aktivite feed: "… giriş sayfasına girdi" */
export function getScreenViewFeedPhrase(activityData, eventTypeFallback = null) {
  const raw =
    resolveScreenNameFromActivityData(activityData, eventTypeFallback) ||
    (looksLikeMobileScreenComponent(eventTypeFallback) ? eventTypeFallback : null);

  if (!raw) return 'bir sayfaya girdi';

  const rawLower = String(raw).trim().toLowerCase();
  if (rawLower === 'webviewpage') {
    return 'alışveriş sayfasına girdi';
  }

  let readable = toReadableScreenName(raw);
  if (!readable) return 'bir sayfaya girdi';

  const label = readable.charAt(0).toLocaleLowerCase('tr-TR') + readable.slice(1);
  return `${label} sayfasına girdi`;
}
